import React, { useState, useEffect, useRef } from 'react';
import ArrowLeft  from '@decisiv/iconix/lib/components/ArrowLeft';
import ArrowRight from '@decisiv/iconix/lib/components/ArrowRight';
import { Button, Flex } from '@decisiv/ui-components';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import AdminPanel from './AdminPanel';

const csrfToken = () =>
  document.querySelector('[name="csrf-token"]')?.getAttribute('content') ?? '';

const PER_PAGE      = 25;
const SEARCH_DELAY  = 300;
// Ransack predicate matching either the event name or its module.
const SEARCH_FIELD  = 'name_or_event_module_cont';

const tableStyle  = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle     = { textAlign: 'left', padding: '8px 12px', fontWeight: 600, borderBottom: '2px solid #e0e0e0', color: '#555' };
const tdStyle     = { padding: '8px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };
const emptyStyle  = { ...tdStyle, textAlign: 'center', color: '#888' };

// Builds the events list URL for a given page and search term, requesting a
// fixed page size and letting the backend ransack-filter by name/module.
const eventsUrl = (endpoint, page, search) => {
  const params = new URLSearchParams({ per_page: PER_PAGE, page });
  if (search) params.set(`q[${SEARCH_FIELD}]`, search);
  return `${endpoint}?${params}`;
};

const SubscriptionPanel = ({ panelConfig, record }) => {
  const [events, setEvents]               = useState([]);
  // Subscribed ids are kept across page changes and searches so toggling on one
  // page never drops selections made on another.
  const [subscribedIds, setSubscribedIds] = useState(record.subscribed_event_ids ?? []);
  const [pagination, setPagination]       = useState({ page: 1, total_pages: 1, total_items: 0 });
  const [page, setPage]                   = useState(1);
  const [searchInput, setSearchInput]     = useState('');
  const [search, setSearch]               = useState('');
  const [loading, setLoading]             = useState(true);
  const [loadError, setLoadError]         = useState(false);
  const [toggling, setToggling]           = useState(null);

  const debounceRef = useRef(null);

  // Debounce search input and reset to page 1 whenever the term changes.
  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearchInput(text);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(text);
    }, SEARCH_DELAY);
  };

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    window.fetch(eventsUrl(panelConfig.eventsEndpoint, page, search), {
      headers: { Accept: 'application/json' },
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => {
        setEvents(data.records ?? []);
        if (data.pagination) setPagination(data.pagination);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [page, search, panelConfig.eventsEndpoint]);

  const toggle = async (event) => {
    const subscribed = subscribedIds.includes(event.id);
    const url        = subscribed
      ? panelConfig.unsubscribeEndpoint(record)
      : panelConfig.subscribeEndpoint(record);

    setToggling(event.id);
    try {
      const res = await window.fetch(`${url}?event_id=${event.id}`, {
        method:  'POST',
        headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        setSubscribedIds((prev) =>
          subscribed ? prev.filter((id) => id !== event.id) : [...prev, event.id]
        );
      }
    } finally {
      setToggling(null);
    }
  };

  const { page: currentPage, total_pages: totalPages, total_items: totalItems } = pagination;

  return (
    <AdminPanel title={panelConfig.title ?? 'Events'}>
      <div style={{ marginBottom: 8 }}>
        <SearchInput
          size="small"
          value={searchInput}
          onChange={handleSearchChange}
          searchOn="change"
        />
      </div>

      {loading ? (
        <div style={{ padding: 8, color: '#888' }}>Loading…</div>
      ) : loadError ? (
        <div style={{ padding: 8, color: '#c0392b', fontSize: 13 }}>
          Failed to load events — please refresh the page.
        </div>
      ) : (
        <>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Description</th>
                <th style={{ ...thStyle, width: 140 }}>Subscription</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={3} style={emptyStyle}>No events found.</td></tr>
              ) : events.map((event) => {
                const subscribed = subscribedIds.includes(event.id);
                return (
                  <tr key={event.id}>
                    <td style={tdStyle}>{event.name}</td>
                    <td style={tdStyle}>{event.description}</td>
                    <td style={tdStyle}>
                      <Button
                        text={subscribed ? 'Unsubscribe' : 'Subscribe'}
                        kind="secondary"
                        intent={subscribed ? 'danger' : undefined}
                        size="small"
                        disabled={toggling === event.id}
                        onClick={() => toggle(event)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalItems > 0 && (
            <Flex alignItems="center" justifyContent="space-between" paddingTop={1}>
              <P shade={1} style={{ margin: 0 }}>
                {(currentPage - 1) * PER_PAGE + 1}–
                {Math.min(currentPage * PER_PAGE, totalItems)} of {totalItems}
              </P>
              {totalPages > 1 && (
                <Flex alignItems="center" gap={1}>
                  <Button
                    icon={ArrowLeft}
                    text="Previous"
                    kind="secondary"
                    size="small"
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                  />
                  <P style={{ margin: '0 8px', whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {totalPages}
                  </P>
                  <Button
                    icon={ArrowRight}
                    iconPosition="right"
                    text="Next"
                    kind="secondary"
                    size="small"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage(currentPage + 1)}
                  />
                </Flex>
              )}
            </Flex>
          )}
        </>
      )}
    </AdminPanel>
  );
};

export default SubscriptionPanel;
